/**
 * Photo UI
 *
 * This class handles templating for the gallery photos and data bindings with
 * the Photo class
 */
goog.provide('yext.location.gallery.photo.ui');

goog.require('yext');
goog.require('yext.dam.assetselector.usage');
goog.require('yext.dam.labels.view');
goog.require('yext.gmb');
goog.require('yext.image');
goog.require('yext.location.helpers');
goog.require('yext.location.photodestination');
goog.require('yext.textfield');
goog.require('yext.textfield.embed');

goog.scope(function() {
  var _ui = yext.location.gallery.photo.ui;

  _ui.imageWidth = 160;
  _ui.imageHeight = 120;

  // Array of objects that maps markup and model for a photo
  _ui.map = [];

  /**
   * @param {!Array.<string>} destinations Array of all Image.Destinations as strings
   */
  _ui.init = function(destinations) {
    _ui.destinations = destinations;
  };

  /**
   * Build image object, size and provide onload callback
   *
   * @param {string} url Image url
   * @param {function()} cb On load callback
   */
  _ui.newImage = function(url, cb) {
    var img = new Image();
    img.onload = function() {
      cb(this);
    };
    img.src = url;
    img.onerror = function() {
      yext.location.helpers.fallbackImage(cb);
    };
  };

  /**
   * Generate markup and do data bindings
   *
   * @param {!jQuery} $context Parent element for the field
   * @param {Photo} photo Photo data model class
   * @param {boolean} hasGMBSubscription boolean to determine whether to show photo constraint warnings
   * @param {number} customFieldId The id if it is a custom field, null otherwise
   *
   * @return {!jQuery} The photo markup
   */
  _ui.create = function($context, photo, hasGMBSubscription, customFieldId) {
    var $photo = _ui.generateMarkup($context, photo, hasGMBSubscription, customFieldId);

    _ui.dataBindings(photo, $photo);

    $(photo).on('updated.photo', function(e, data) {
      _ui.render(this, data);
    });

    _ui.map.push({$markup: $photo, model: photo});

    return $photo;
  };

  /**
   * Generate the markup for a photo model
   *
   * @param {!jQuery} $context Parent element for the field
   * @param {Photo} photo The photo model for which to generate markup
   * @param {boolean} hasGMBSubscription boolean to determine whether to show photo constraint warnings
   * @param {number} customFieldId The id if it is a custom field, null otherwise
   */
  _ui.generateMarkup = function($context, photo, hasGMBSubscription, customFieldId) {
    var $photo = $($context.find('.js-gallery-edit-photo-template').html());
    var photoPreview = photo.url;

    if (photo.croppedImage) {
      photoPreview = photo.croppedImage;
    }
    // Build and size image
    _ui.newImage(yext.getDynamicThumbnailUrl(photoPreview, _ui.imageWidth, _ui.imageHeight), function(img) {
      $photo.find('.js-photo-grid-item').prepend(img);
    });

    // Set description and clickthrough url
    var $description = _ui.getDescription($photo);
    var $details = _ui.getDetails($photo);
    var $clickthroughUrl = _ui.getClickthroughUrl($photo);
    var $alternateText = _ui.getAlternateText($photo);

    // Get correct placeholder text if indeterminate
    if (photo.descriptionIndeterminate) {
      $description.attr({
        'data-default-placeholder': $description.prop('placeholder'),
        'placeholder': $description.data('various-placeholder'),
      });
    }

    if (photo.detailsIndeterminate) {
      $details.attr({
        'data-default-placeholder': $details.prop('placeholder'),
        'placeholder': $details.data('various-placeholder'),
      });
    }

    // Get correct placeholder text if indeterminate
    if (photo.clickthroughUrlIndeterminate) {
      $clickthroughUrl.attr({
        'data-default-placeholder': $clickthroughUrl.prop('placeholder'),
        'placeholder': $clickthroughUrl.data('various-placeholder'),
      });
    }

    if (photo.alternateTextIndeterminate) {
      $alternateText.attr({
        'data-default-placeholder': $alternateText.prop('placeholder'),
        'placeholder': $alternateText.data('various-placeholder'),
      });
    }

    $description
      .val(photo.description)
      .placeholder();

    $details
      .val(photo.details)
      .placeholder();

    $clickthroughUrl
      .val(photo.clickthroughUrl)
      .placeholder();

    $alternateText
      .val(photo.alternateText)
      .placeholder();

    // Build and set photo destinations
    var $photoDestinations = $photo.find('.js-photo-destinations');
    var $photoDestinationsIndeterminate = $photo.find('.js-pd-indeterminate-message');
    var $resetDestinations = $photo.find('.js-reset-pd');

    var photoDestination = yext.location.photodestination.create(photo.destinations);

    // Only show if there is more than 1 destination
    if (photoDestination.destinationCount > 1) {
      $photoDestinations.append(photoDestination.$container);
    }

    $resetDestinations.click(function() {
      $photoDestinationsIndeterminate.hide();
      $photoDestinations.show();
    });

    $photoDestinations.toggle(!photo.destinationIndeterminate);
    $photoDestinationsIndeterminate.toggle(photo.destinationIndeterminate);

    if (photo.indeterminate) {
      $photo.find('.js-gallery-edit-checkbox').prop({
        checked: false,
        indeterminate: true,
      });
    }

    var parentFieldId = $context.find('.js-gallery-fields').find('[name="field"]').val();
    var usageType = goog.module.get('yext.dam.assetselector.usage').forProfileFields(parentFieldId);

    // Create text field instance for description and clickthrough url
    var clickthroughUrl = yext.textfield.create($clickthroughUrl.closest('.js-text-field'), undefined, customFieldId, undefined, usageType);
    var description = yext.textfield.create($description.closest('.js-text-field'), undefined, customFieldId, undefined, usageType);
    var details = yext.textfield.create($details.closest('.js-text-field'), undefined, customFieldId, undefined, usageType);
    var alternateText = yext.textfield.create($alternateText.closest('.js-text-field'), undefined, customFieldId, undefined, usageType);

    _ui.appendPhotoTooltip($photo, photo, hasGMBSubscription);

    return $photo;
  };

  /**
   * Add tooltip to photo depending on whether photo has assets and/or
   * we need to show constraint warnings
   *
   * @param {jqueryObject} $photo jquery DOM object to append tooltip to
   * @param {Photo} photo Photo model to show tooltip for
   * @param {boolean} showConstraintWarning whether to show image constraint warnings or not
   */
  _ui.addLabel = function($photo, photo, showConstraintWarning) {
    if (showConstraintWarning || (photo.asset && photo.asset.id)) {
      var $tooltip = yext.dam.labels.view.getAssetViewLabel(photo.asset, showConstraintWarning);
      if ($tooltip) {
        $photo.find('.js-photo-grid-item').append($tooltip);
      }
    }
  };

  /**
   * Show a tooltip with additional information regarding the photo
   *
   * @param {jqueryObject} $photo jquery DOM object to append tooltip to
   * @param {Photo} photo Photo model to show tooltip for
   * @param {boolean} hasGMBSubscription whether or not the business has GMB subscription
   */
  _ui.appendPhotoTooltip = function($photo, photo, hasGMBSubscription) {
    if (hasGMBSubscription) {
      yext.image.getImageDimensions(photo.url, function(dimensions) {
        var valid = yext.image.areValidDimensions(yext.gmb.MINIMUM_SIZE_CONSTRAINTS, dimensions);
        _ui.addLabel($photo, photo, !valid);
      });
    } else {
      _ui.addLabel($photo, photo, false);
    }
  };

  /**
   * Binds DOM to Photo and listens for Photo 'updates' to render
   * the view
   *
   * @param {Photo} photo Photo model to data bind to
   * @param {!jQuery} $photo Markup to bind from
   */
  _ui.dataBindings = function(photo, $photo) {
    // Bind description and clickthrough url
    var descriptionTextField = yext.textfield.get(_ui.getDescription($photo).closest('.js-text-field'));
    var detailsTextField = yext.textfield.get(_ui.getDetails($photo).closest('.js-text-field'));
    var clickthroughUrl = yext.textfield.get(_ui.getClickthroughUrl($photo).closest('.js-text-field'));
    var alternateTextField = yext.textfield.get(_ui.getAlternateText($photo).closest('.js-text-field'));

    $(descriptionTextField).on('update.textField', function() {
      photo.update({
        description: descriptionTextField.value(),
        descriptionAssetId: descriptionTextField.assetId(),
        descriptionIndeterminate: false,
      });
    });

    $(detailsTextField).on('update.textField', function() {
      photo.update({
        details: detailsTextField.value(),
        detailsAssetId: detailsTextField.assetId(),
        detailsIndeterminate: false,
      });
    });

    $(clickthroughUrl).on('update.textField', function() {
      photo.update({
        clickthroughUrl: clickthroughUrl.value(),
        clickthroughUrlAssetId: clickthroughUrl.assetId(),
        clickthroughUrlIndeterminate: false,
      });
    });

    $(alternateTextField).on('update.textField', function() {
      photo.update({
        alternateText: alternateTextField.value(),
        alternateTextAssetId: alternateTextField.assetId(),
        alternateTextIndeterminate: false,
      });
    });

    var photoDestination = yext.location.photodestination.get($photo.find('.js-pd-container'));

    $(photoDestination).on('update.photoDestination', function() {
      photo.update({
        destinations: photoDestination.destinations,
      });
    });

    $photo.find('.js-reset-pd').click(function() {
      photo.update({
        destinationIndeterminate: false,
      });
    });

    var $checkbox = $photo.find('.js-gallery-edit-checkbox');

    $checkbox.on('click change', function() {
      var update = {
        checked: $checkbox.prop('checked'),
      };

      if (update.checked === true) {
        update.indeterminate = false;
      } else if (update.checked == undefined) {
        update.indeterminate = true;
      }

      photo.update(update);
    });
  };

  /**
   * Render the photo with new data
   *
   * @param {Photo} photo Photo model that is being rendered
   * @param {!Object} data Object that contains updates made to the model
   */
  _ui.render = function(photo, data) {
    var $photo = _ui.getElement(photo);

    if ('description' in data) {
      yext.textfield.get(_ui.getDescription($photo).closest('.js-text-field')).set({
        value: photo.description,
        assetId: photo.descriptionAssetId,
      });
    }

    if ('details' in data) {
      yext.textfield.get(_ui.getDetails($photo).closest('.js-text-field')).set({
        value: photo.details,
        assetId: photo.detailsAssetId,
      });
    }

    if ('clickthroughUrl' in data) {
      yext.textfield.get(_ui.getClickthroughUrl($photo).closest('.js-text-field')).set({
        value: photo.clickthroughUrl,
        assetId: photo.clickthroughUrlAssetId,
      });
    }

    if ('alternateText' in data) {
      yext.textfield.get(_ui.getAlternateText($photo).closest('.js-text-field')).set({
        value: photo.alternateText,
        assetId: photo.alternateTextAssetId,
      });
    }

    if ('destinations' in data) {
      yext.location.photodestination.get($photo.find('.js-pd-container')).set(photo.destinations);
    }

    if ('checked' in data) {
      var $checkbox = $photo.find('.js-gallery-edit-checkbox');

      if ($checkbox.prop('checked') != photo.checked) {
        var props = {indeterminate: false};

        if (photo.checked === true) {
          props.checked = true;
        } else if (photo.checked === false) {
          props.checked = false;
        } else {
          props.indeterminate = true;
          props.checked = false;
        }

        $checkbox.prop(props);
      }
    }

    if ('url' in data) {
      _ui.newImage(yext.getDynamicThumbnailUrl(photo.url, _ui.imageWidth, _ui.imageHeight), function(img) {
        $photo.find('.js-photo-grid-item').empty().append(img);
      });
    }
  };

  /**
   * Call resetState on all photos
   */
  _ui.resetAll = function() {
    for (var i = 0; i < _ui.map.length; i++) {
      _ui.resetState(_ui.map[i].model, _ui.map[i].$markup);
    }
  };

  /**
   * Reset the state of a photo (used in render and when the edit panel is closed)
   * When a photo has indeterminate destinations this handles showing the reset
   * message when they have not set the destinations during edit.
   *
   * @param {Photo} photo Photo model that is being rendered
   *
   */
  _ui.resetState = function(photo, $photo) {
    var $photoDestinations = $photo.find('.js-photo-destinations');
    var $photoDestinationsIndeterminate = $photo.find('.js-pd-indeterminate-message');

    $photoDestinations.toggle(!photo.destinationIndeterminate);
    $photoDestinationsIndeterminate.toggle(photo.destinationIndeterminate);
  };

  /**
   * Hides all the photo destinations for when they are not in use
   */
  _ui.hideDestinations = function(photos) {
    for (var i = 0; i < photos.length; i++) {
      $(photos[i]).find('.js-photo-destinations').hide();
    }
  };

  /**
   * Get the element paired with the model
   *
   * @param {Photo} model Photo model by which to search
   */
  _ui.getElement = function(model) {
    for (var i = 0; i < _ui.map.length; i++) {
      if (_ui.map[i].model == model) {
        return _ui.map[i].$markup;
      }
    }
  };

  /**
   * Get the model paired with the element
   *
   * @param {!jQuery} $element The element by which to search
   */
  _ui.getModel = function($element) {
    for (var i = 0; i < _ui.map.length; i++) {
      if (_ui.map[i].$markup[0] == $element[0]) {
        return _ui.map[i].model;
      }
    }
  };

  /**
   * Remove a photo by removing from the map and removing the markup
   *
   * @param {Photo} model The photo to be removed
   */
  _ui.remove = function(model) {
    for (var i = 0; i < _ui.map.length; i++) {
      if (_ui.map[i].model == model) {
        _ui.map[i].$markup.find('.js-text-field').each(function() {
          yext.textfield.get($(this)).destroy();
        });

        var photoDestination = yext.location.photodestination.get(_ui.map[i].$markup.find('.js-pd-container'));
        photoDestination && photoDestination.destroy();

        _ui.map[i].$markup.remove();
        _ui.map.splice(i, 1);
        return;
      }
    }
  };

  /**
   * Generate the view markup for a photo
   *
   * @param {Photo} photo The photo model for which to generate markup
   * @param {boolean} hasGMBSubscription boolean to determine whether to show photo constraint warnings
   */
  _ui.generateViewMarkup = function($context, photo, hasGMBSubscription) {
    var $photo = $($context.find('.js-gallery-display-photo-template').html());
    var $displayVariousMessage = $photo.find('.js-photo-destinations');

    // Build and size image
    _ui.newImage(yext.getDynamicThumbnailUrl(photo.url, _ui.imageWidth, _ui.imageHeight), function(img) {
      var imageLoaded = yext.imageLoaded(img);
      var $img;

      if (!imageLoaded) {
        $img = $(img).hide();
      }

      $photo.find('.js-photo-grid-item').prepend(img);

      _ui.appendPhotoTooltip($photo, photo, hasGMBSubscription);

      if (!imageLoaded) {
        $img.fadeIn();
      }
    });

    var $photoDestinations = $photo.find('.js-photo-destinations').empty();

    if (!photo.destinationIndeterminate) {
      $photoDestinations.append(yext.location.photodestination.viewMarkup(photo.destinations));
    } else {
      $photoDestinations.append($displayVariousMessage);
    }

    var $description = $photo.find('.js-photo-display-caption');
    var $details = $photo.find('.js-photo-display-details');
    var $clickthroughUrl = $photo.find('.js-photo-display-url');
    var $alternateText = $photo.find('.js-photo-display-alternatetext');

    _ui.renderPhotoTextField(
      $description,
      photo.description);
    _ui.renderPhotoTextField(
      $details,
      photo.details);
    _ui.renderPhotoTextField(
      $clickthroughUrl,
      photo.clickthroughUrl);
    _ui.renderPhotoTextField(
      $alternateText,
      photo.alternateText);

    return $photo;
  };

  /**
   * Renders a view-only textfield
   *
   * @param {!jQuery} $el The photo text field element
   * @param {string} content The content of the field
   */
  _ui.renderPhotoTextField = function($el, content) {
    if (content) {
      $el.find('.js-photo-text-field-content').text(content);
      $el.show();
    } else {
      $el.hide();
    }
  };

  /**
   * Checks to see if there is only one destination for all photos
   *
   * @return {boolean} True if there is only one selected or eligibe destination
   * for all photos
   */
  _ui.singleDestination = function() {
    for (var i = 0; i < _ui.map.length; i++) {
      var photo = _ui.map[i].model;
      var count = 0;
      var destinations = yext.location.photodestination.destinations;

      for (var j = 0; j < destinations.length; j++) {
        var selected = photo.destinations.indexOf(destinations[j].value) != -1;
        if (selected || destinations[j].eligible) {
          if (++count > 1) {
            return false;
          }
        }
      }
    }
    return true;
  };

  /**
   * Gets the description text field
   *
   * @param {!jQuery} $photo The photo element
   * @return {!jQuery} The photo description text field element
   */
  _ui.getDescription = function($photo) {
    return $photo.find('.js-photo-edit-description .js-text-field-input');
  };

  /**
   * Gets the photo details text field
   *
   * @param {!jQuery} $photo The photo element
   * @return {!jQuery} The photo details text field element
   */
  _ui.getDetails = function($photo) {
    return $photo.find('.js-photo-edit-details .js-text-field-input');
  };

  /**
   * Gets the photo clickthrough url text field
   *
   * @param {!jQuery} $photo The photo element
   * @return {!jQuery} The photo clickthrough url text field element
   */
  _ui.getClickthroughUrl = function($photo) {
    return $photo.find('.js-photo-edit-clickthrough-url .js-text-field-input');
  };

  /**
   * Gets the photo alternate text field
   *
   * @param {!jQuery} $photo The photo element
   * @return {!jQuery} The photo alternate text field element
   */
  _ui.getAlternateText = function($photo) {
    return $photo.find('.js-photo-edit-alternatetext .js-text-field-input');
  };
});
